import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import "../Accounts/ledger-page.css";
import { PAGE_SIZE_OPTIONS } from "../../constants/pageSizeOptions";
import SearchableSelect from "../Common/SearchableSelect";
import useApi from "../../hook/useApi";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

const SortHint = () => (
  <Icon
    icon="tabler:chevron-down"
    className="icon ms-1 text-secondary align-middle"
    style={{ fontSize: "0.65rem" }}
  />
);

const RejectionLogLayer = () => {
  const { AsyncGetApiCall } = useApi();
  const [filters, setFilters] = useState({
    client: "",
    fromDate: "",
    toDate: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [limitMode, setLimitMode] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [pageMeta, setPageMeta] = useState({
    total: 0,
    totalPages: 0,
    currentPage: 1,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const limitVal = limitMode === "all" ? 50 : Number(limitMode);

  const fetchRejectionLog = useCallback(async () => {
    setLoading(true);
    try {
      const payload = { pageNo, limitVal };
      if (searchTerm.trim()) payload.search = searchTerm.trim();
      if (filters.client) payload.accountId = filters.client;
      if (filters.fromDate) payload.fromDate = filters.fromDate;
      if (filters.toDate) payload.toDate = filters.toDate;

      const result = await AsyncGetApiCall(
        "/orders/rejection-logs",
        payload,
      );

      if (result?.success) {
        setRows(Array.isArray(result?.data) ? result?.data : []);
        setPageMeta({
          total: Number(result?.total) || 0,
          totalPages: Number(result?.totalPages) || 0,
          currentPage: Number(result?.currentPage) || pageNo,
          hasNextPage: Boolean(result?.hasNextPage),
          hasPrevPage: Boolean(result?.hasPrevPage),
        });
      } else {
        setRows([]);
        setPageMeta({
          total: 0,
          totalPages: 0,
          currentPage: 1,
          hasNextPage: false,
          hasPrevPage: false,
        });
      }
    } catch (error) {
      console.error("Failed to fetch rejection logs", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [
    AsyncGetApiCall,
    filters.client,
    filters.fromDate,
    filters.toDate,
    limitVal,
    pageNo,
    searchTerm,
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchRejectionLog();
    }, 350);
    return () => clearTimeout(timer);
  }, [fetchRejectionLog]);

  const total = pageMeta.total || rows.length;
  const hasPrev = pageMeta.hasPrevPage;
  const hasNext = pageMeta.hasNextPage;

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPageNo(1);
  };

  const handleApply = () => {
    setPageNo(1);
    fetchRejectionLog();
  };

  const handleClear = () => {
    setFilters({
      client: "",
      fromDate: "",
      toDate: "",
    });
    setSearchTerm("");
    setPageNo(1);
  };

  const [clientOptions, setClientOptions] = useState([]);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await AsyncGetApiCall("/users/dynamic-list", {
          listType: "user",
          limitVal: 5000,
        });
        if (res?.success && Array.isArray(res.data)) {
          const options = res.data.map((user) => ({
            label: `${user.name || user.masterName || "User"} (${user.loginId})`,
            value: user._id || user.userId,
          }));
          setClientOptions(options);
        }
      } catch (error) {
        console.error("Failed to fetch clients", error);
      }
    };
    fetchClients();
  }, [AsyncGetApiCall]);

  const formatDateTime = (value) => {
    if (!value) return "—";
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return "—";
    return dt.toLocaleString();
  };

  const formatAmount = (value) => {
    if (value == null) return "—";
    const num = Number(value);
    if (Number.isNaN(num)) return "—";
    return num.toLocaleString("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  const startEntry =
    total === 0 ? 0 : (pageMeta.currentPage - 1) * limitVal + 1;
  const endEntry =
    total === 0 ? 0 : (pageMeta.currentPage - 1) * limitVal + rows.length;

  return (
    <div className="card border shadow-sm radius-16 ledger-table-card">
      <div className="card-body p-20 border-bottom position-relative" style={{ zIndex: 10 }}>
        <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-between gap-3">
          <div className="d-flex align-items-end gap-2 flex-wrap">
            <div>
              <label className="form-label fw-bold text-primary-light mb-4 d-block text-uppercase small">
                Show
              </label>
              <div style={{ width: "78px" }}>
                <SearchableSelect
                  placeholder="All"
                  value={limitMode}
                  showSearch={false}
                  onChange={(e) => {
                    setLimitMode(e.target.value);
                    setPageNo(1);
                  }}
                  options={PAGE_SIZE_OPTIONS.map((n) => ({
                    label: String(n),
                    value: String(n),
                  }))}
                />
              </div>
            </div>

            <div>
              <label className="form-label fw-bold text-primary-light mb-4 d-block text-uppercase small">
                Select client
              </label>
              <div style={{ minWidth: "180px" }}>
                <SearchableSelect
                  name="client"
                  placeholder="All users"
                  value={filters.client}
                  showSearch={true}
                  onChange={handleFilterChange}
                  options={clientOptions}
                />
              </div>
            </div>

            <div>
              <label className="form-label fw-bold text-primary-light mb-4 d-block text-uppercase small">
                From date
              </label>
              <input
                type="date"
                name="fromDate"
                value={filters.fromDate}
                onChange={handleFilterChange}
                className="form-control radius-8 h-40-px"
                style={{ width: "122px" }}
              />
            </div>

            <div>
              <label className="form-label fw-bold text-primary-light mb-4 d-block text-uppercase small">
                To date
              </label>
              <input
                type="date"
                name="toDate"
                value={filters.toDate}
                onChange={handleFilterChange}
                className="form-control radius-8 h-40-px"
                style={{ width: "122px" }}
              />
            </div>

            <button
              type="button"
              onClick={handleApply}
              className="btn btn-sm radius-8 px-16 py-10 btn-primary-600"
              disabled={loading}
            >
              Filter Results
            </button>
            <button
              type="button"
              onClick={handleClear}
              className="btn btn-sm radius-8 px-16 py-10 btn-danger-600"
              disabled={loading}
            >
              Clear Filters
            </button>
          </div>

          <div
            className="position-relative flex-grow-1"
            style={{ maxWidth: "280px" }}
          >
            <Icon
              icon="ion:search-outline"
              className="icon position-absolute top-50 start-0 translate-middle-y ms-12 text-secondary-light"
            />
            <input
              type="search"
              className="form-control ps-40 h-40-px radius-12"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPageNo(1);
              }}
            />
          </div>
        </div>
      </div>

      <div className="table-responsive p-24">
        <table className="table bordered-table sm-table mb-0 text-nowrap">
          <thead>
            <tr className="bg-neutral-100">
              <th className="fw-bold text-primary-light">
                SR No <SortHint />
              </th>
              <th className="fw-bold text-primary-light">
                Order ID <SortHint />
              </th>
              <th className="fw-bold text-primary-light">
                Client <SortHint />
              </th>
              <th className="fw-bold text-primary-light">
                Details <SortHint />
              </th>
              <th className="fw-bold text-primary-light">
                Script <SortHint />
              </th>
              <th className="fw-bold text-primary-light">
                Segment <SortHint />
              </th>
              <th className="fw-bold text-primary-light">
                Lot <SortHint />
              </th>
              <th className="fw-bold text-primary-light">
                Qty <SortHint />
              </th>
              <th className="fw-bold text-primary-light">
                Price <SortHint />
              </th>
              <th className="fw-bold text-primary-light">
                Remark <SortHint />
              </th>
              <th className="fw-bold text-primary-light">
                Date <SortHint />
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={10}
                  className="text-center py-5 text-secondary-light"
                >
                  Loading...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={10}
                  className="text-center py-5 text-secondary-light"
                >
                  No data available in table
                </td>
              </tr>
            ) : (
              rows.map((entry, index) => (
                <tr key={entry?._id || `${entry?.userId}-${index}`}>
                  <td>{(pageMeta.currentPage - 1) * limitVal + index + 1}</td>
                  <td>{entry?.orderId || "—"}</td>
                  <td>{`${(entry?.userId?.name || entry?.userId?.masterName)} (${entry?.userId?.loginId})`}</td>
                  <td>{`${entry?.side} (${entry?.orderType})` || "—"}</td>
                  <td>{entry?.symbol || "—"}</td>
                  <td>{entry?.segment || "—"}</td>
                  <td>{entry?.inputLot || "—"}</td>
                  <td>{entry?.quantity || "—"}</td>
                  <td>{entry?.orderPrice || "—"}</td>
                  <td>
                    <>
                      {entry?.remark?.length > 0 ?
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip>
                              {entry?.remark?.length > 0 ? entry?.remark : "—"}
                            </Tooltip>
                          }
                        >
                          <span style={{ cursor: "pointer" }}>
                            {entry.remark.substring(0, 20)}...
                          </span>
                        </OverlayTrigger> : entry?.remark || "—"
                      }
                    </>
                  </td>
                  <td>{formatDateTime(entry?.createdAt)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="border-top d-flex flex-column flex-sm-row align-items-center justify-content-between gap-3 p-24">
        <p className="mb-0 text-secondary-light small">
          Showing{" "}
          <span className="fw-medium text-primary-light">
            {startEntry} to {endEntry} of {total} entries
          </span>
        </p>
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary radius-8 px-16 py-8 d-inline-flex align-items-center gap-1"
            disabled={!hasPrev}
            onClick={() => setPageNo((prev) => Math.max(1, prev - 1))}
          >
            <Icon icon="tabler:chevron-left" className="icon" />
            Prev
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary radius-8 px-16 py-8 d-inline-flex align-items-center gap-1"
            disabled={!hasNext}
            onClick={() => setPageNo((prev) => prev + 1)}
          >
            Next
            <Icon icon="tabler:chevron-right" className="icon" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default RejectionLogLayer;
